import BlogHeader from '@/components/BlogHeader'
import Main from '@/components/Main'
import PostFeed from '@/components/PostFeed'
import SearchForm from '@/components/SearchForm'
import SEO from '@/components/SEO'
import { FeaturedImageFragment } from '@/fragments/FeaturedImage'
import styles from '@/styles/pages/_default.module.scss'
import { formatCategoryName } from '@/utilities/contentHelpers'
import { gql, useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react'

const POSTS_PER_PAGE = 12

const BLOG_POSTS_QUERY = gql`
  ${FeaturedImageFragment}
  query GetBlogPosts($first: Int!, $after: String, $search: String, $categoryName: String) {
    posts(first: $first, after: $after, where: { search: $search, categoryName: $categoryName }) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          date
          uri
          title
          excerpt
          categories {
            nodes {
              name
            }
          }
          ...FeaturedImageFragment
          postFields {
            blogSliderImage {
              node {
                sourceUrl
                altText
              }
            }
          }
        }
      }
    }
  }
`

function safeSessionGet(key) {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSessionSet(key, value) {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    /* storage unavailable (e.g. Safari private mode) */
  }
}

function safeSessionRemove(key) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* storage unavailable */
  }
}

export default function BlogPage(props) {
  const router = useRouter()
  const [currentSearchTerm, setCurrentSearchTerm] = useState('')
  const [currentCategory, setCurrentCategory] = useState('')
  const debounceRef = useRef(null)

  useEffect(() => {
    const savedTerm = safeSessionGet('searchTerm') || ''
    const savedCategory = safeSessionGet('searchCategory') || ''
    if (savedTerm || savedCategory) {
      startTransition(() => {
        setCurrentSearchTerm(savedTerm)
        setCurrentCategory(savedCategory)
      })
    }
  }, [])

  const { data, loading, fetchMore } = useQuery(BLOG_POSTS_QUERY, {
    variables: {
      first: POSTS_PER_PAGE,
      search: currentSearchTerm || undefined,
      categoryName: currentCategory || undefined
    },
    notifyOnNetworkStatusChange: true
  })

  const ssrPosts = props?.data?.posts?.nodes ?? []
  const queryEdges = data?.posts?.edges
  const queryPosts = useMemo(() => queryEdges?.map((edge) => edge.node) ?? [], [queryEdges])

  const hasActiveFilters = !!currentSearchTerm || !!currentCategory
  const hasMoreThanSSR = queryPosts.length > ssrPosts.length
  const posts = (hasActiveFilters || hasMoreThanSSR) && queryEdges ? queryPosts : ssrPosts

  const queryPageInfo = data?.posts?.pageInfo
  const propsPageInfo = props?.data?.posts?.pageInfo
  const pageInfo = useMemo(() => queryPageInfo ?? propsPageInfo ?? {}, [queryPageInfo, propsPageInfo])

  useEffect(() => {
    const savedScrollY = parseInt(safeSessionGet('scrollY') || '0', 10)
    if (savedScrollY > 0 && !loading && posts.length > 0) {
      const timer = setTimeout(() => {
        window.scrollTo(0, savedScrollY)
        safeSessionRemove('scrollY')
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [loading, posts.length])

  useEffect(() => {
    const saveScrollPosition = () => {
      safeSessionSet('scrollY', window.scrollY.toString())
    }

    const handleRouteChangeStart = () => saveScrollPosition()

    router.events.on('routeChangeStart', handleRouteChangeStart)
    window.addEventListener('beforeunload', saveScrollPosition)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      window.removeEventListener('beforeunload', saveScrollPosition)
    }
  }, [router.events])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleSearch = useCallback((searchTerm, selectedCategory) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      const resolvedCategory = selectedCategory && formatCategoryName(selectedCategory).toLowerCase() !== 'view all' ? selectedCategory : ''

      safeSessionSet('searchTerm', searchTerm)
      safeSessionSet('searchCategory', resolvedCategory)
      setCurrentSearchTerm(searchTerm)
      setCurrentCategory(resolvedCategory)
      safeSessionSet('scrollY', '0')
    }, 300)
  }, [])

  const handleViewMore = useCallback(() => {
    if (!pageInfo.hasNextPage || loading) return

    fetchMore({
      variables: {
        after: pageInfo.endCursor
      }
    })
  }, [fetchMore, pageInfo, loading])

  if (props.loading) {
    return null
  }

  const metaDesc = props?.data?.page?.seo?.metaDesc
  const { title, featuredImage } = props?.data?.page ?? { title: '' }
  const categories = props?.data?.categories
  const seoTitle = props?.data?.page?.seo?.title

  return (
    <>
      <SEO title={seoTitle || title} description={metaDesc} imageUrl={featuredImage?.node?.sourceUrl} />

      <BlogHeader pageFields={props?.data?.page} image={featuredImage?.node} disableOverlay={true} />

      <Main>
        <div className={`container ${styles.workContainer}`}>
          <SearchForm
            categories={categories}
            onSearch={handleSearch}
            initialSearchTerm={currentSearchTerm}
            initialCategory={currentCategory}
          />
          <div className={styles.blogFeed}>
            <section className={styles.posts}>
              <PostFeed posts={posts} id="posts-list" onViewMore={handleViewMore} canViewMore={pageInfo.hasNextPage && !loading} />
            </section>
          </div>
        </div>
      </Main>
    </>
  )
}

BlogPage.variables = ({ databaseId }, ctx) => {
  return {
    databaseId,
    asPreview: ctx?.asPreview
  }
}

BlogPage.query = gql`
  ${FeaturedImageFragment}
  query GetPageData($databaseId: ID!, $asPreview: Boolean = false) {
    page(id: $databaseId, idType: DATABASE_ID, asPreview: $asPreview) {
      title
      content
      seo {
        metaDesc
        title
      }
      ...FeaturedImageFragment
      innerHeroFields {
        content
        eyebrowText
        fieldGroupName
        includeForm
        title
      }
    }
    posts(first: 12) {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        date
        uri
        title
        excerpt
        categories {
          nodes {
            name
          }
        }
        ...FeaturedImageFragment
        postFields {
          blogSliderImage {
            node {
              sourceUrl
              altText
            }
          }
        }
      }
    }
    categories(first: 5) {
      nodes {
        name
      }
    }
  }
`
